/**
 * Mounts the original app's screens (Purchase Calculator, Reminders, Doctor
 * Visits, Tests, Library) inside Bhorosha. Their code runs unchanged except
 * that storage goes through `window.BhStore` (see scripts/extract-legacy.mjs).
 */
import markup from './generated/markup.html?raw';
import styles from './generated/styles.css?raw';
import script from './generated/app.js.txt?raw';
import type { BhStore } from '@bhorosha/data';

declare global {
  interface Window {
    BhStore: BhStore;
    __bhReload?: () => void;
    __bhSwitchPerson?: (id: string) => void;
    __bhToast?: (msg: string) => void;
    state?: { people: { id: string; name: string }[]; activePersonId: string; inlineEdit: unknown };
    /** The signed-in user, for the original screens. */
    __bhMe?: { userId: string; displayName: string; role: string };
    /** The family's members (for "who gets notifications"). */
    __bhMembers?: { userId: string; displayName: string; email: string; role: string; allPatients: boolean; patientIds: string[] }[];
    __bhMembersChanged?: () => void;
    /** Phone problems for the bell (alerts blocked, not synced): see monitor/phoneWatch.ts */
    __bhPhoneWarnings?: { key: string; userId: string; title: string; sub: string }[];
  }
}

let mounted = false;

export function mountLegacyApp(store: BhStore, host: HTMLElement) {
  if (mounted) throw new Error('legacy app already mounted');
  mounted = true;
  window.BhStore = store;

  const style = document.createElement('style');
  style.id = 'bh-legacy-styles';
  style.textContent = styles;
  document.head.appendChild(style);

  host.innerHTML = markup;

  const el = document.createElement('script');
  el.id = 'bh-legacy-script';
  el.textContent = script;
  document.body.appendChild(el); // runs synchronously, in global scope
}

/** True while the user is in the middle of something (a dialog, an inline edit, typing). */
export function legacyIsBusy(): boolean {
  if (document.querySelector('.modal-overlay.open')) return true;
  if (window.state?.inlineEdit) return true;
  const pop = document.getElementById('rpPop');
  if (pop && !pop.hidden) return true;
  const a = document.activeElement as HTMLElement | null;
  if (a && a.closest('#bh-legacy') && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT')) return true;
  return false;
}

let reloadTimer: ReturnType<typeof setInterval> | null = null;
/** Re-render the original screens with fresh data, waiting until the user isn't mid-edit. */
export function requestLegacyReload() {
  if (!mounted || reloadTimer) return;
  const tryNow = () => {
    if (legacyIsBusy()) return false;
    if (reloadTimer) { clearInterval(reloadTimer); reloadTimer = null; }
    window.__bhReload?.();
    return true;
  };
  if (!tryNow()) reloadTimer = setInterval(tryNow, 1500);
}

export function legacyToast(msg: string) {
  if (window.__bhToast) window.__bhToast(msg); else console.info(msg);
}
